import {
  DiscountedValue,
  DiscountSavingRow,
  MMButton,
  MMLabelValue,
  PreviewLayout,
  SendMoneyAmountRow,
  TrustedRecipientCheckbox
} from '@components'
import { getAccountingCategoryLabel } from '@constants'
import { useFeatureFlag, useWithdrawPromoCode } from '@hooks'
import { formatCurrencyForDisplay } from '@utils'
import styles from '@withdrawals/styles/WithdrawPreview.module.scss'
import {
  useCurrencies,
  useFeatureFlagState,
  useWalletTransferPreviewQry,
  useWithdrawPreviewMutation
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse, RecipientResponse, WithdrawalSubFlow } from 'mmfintech-backend-api/types'
import { formatFloat, formatIban, tr, translateCountryName } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useState } from 'react'

type WalletTransferPreviewProps = {
  formValues: UseFormValuesOutput
  selectedAccount: AccountBalanceResponse
  selectedRecipient: RecipientResponse
  onSubmit: () => void
  onCancel: () => void
  /** Optional callback when promo code is applied - can be sync or async */
  onApplyPromoCode?: (code: string) => void | Promise<void>
  subFlow?: WithdrawalSubFlow
}

const WalletTransferPreview = (props: WalletTransferPreviewProps) => {
  const { formValues, selectedAccount, selectedRecipient, onSubmit, onCancel, onApplyPromoCode, subFlow } = props

  const { getCurrencyPrecision } = useCurrencies()
  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  const hasWithdrawPromoCode = useFeatureFlag('withdrawPromoCode')

  const { transferPreview, transferError, transferFetching } = useWalletTransferPreviewQry()

  // Track promo code state for clear functionality
  const [promoCode, setPromoCode] = useState('')

  // Promo code validation hook (combines stable preview + error handling + validation logic)
  const {
    displayPreview,
    validatePromoCode,
    isLoading: isPromoLoading,
    errorMessage: promoErrorMessage
  } = useWithdrawPromoCode({
    mutation: useWithdrawPreviewMutation,
    subFlow,
    previewData: transferPreview,
    buildRequest: (code: string) => {
      const allFormValues = formValues.prepare()
      return {
        ...allFormValues,
        amount: parseFloat(allFormValues.amount),
        sourceAccountId: selectedAccount?.id,
        destinationPaymentInstrumentId: selectedRecipient?.paymentInstrumentId,
        promoCode: code
      }
    },
    onSuccess: async (code: string) => {
      setPromoCode(code)
      if (onApplyPromoCode) {
        await onApplyPromoCode(code)
      }
    }
  })

  // Use displayPreview for all UI rendering (stable, won't nullify on errors)
  const {
    fee,
    feeCurrency,
    processingAmount,
    processingCurrency,
    walletAmount,
    walletCurrency,
    trustedRecipient,
    originalFeeAmount,
    promoCodeDiscount
  } = displayPreview || {}

  const showPromo = hasWithdrawPromoCode && !!onApplyPromoCode && Number(originalFeeAmount || fee) > 0
  const isPromoApplied = !!promoCode && Number(promoCodeDiscount) > 0

  return (
    <PreviewLayout
      showPromo={showPromo}
      promoCodeConfig={{
        validatePromoCode,
        isLoading: isPromoLoading,
        error: promoErrorMessage,
        isApplied: isPromoApplied,
        onClear: () => setPromoCode('')
      }}
    >
      <div className={styles['withdraw-preview-wrapper']}>
        <div className='withdraw-preview-container amount-container'>
          <div className='main-amount-label'>{tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.AMOUNT', 'Amount')}</div>
          <div className='main-amount-value'>
            <span data-test='amount'>
              {formatFloat(walletAmount, getCurrencyPrecision(walletCurrency))}{' '}
              <span className='main-amount-currency'>{formatCurrencyForDisplay(walletCurrency)}</span>
            </span>
          </div>

          {(Number(originalFeeAmount) > 0 || fee > 0) && (
            <SendMoneyAmountRow
              label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.FEE', 'Fee')}
              value={
                <DiscountedValue
                  value={formatFloat(originalFeeAmount || fee, getCurrencyPrecision(feeCurrency))}
                  discountedValue={formatFloat(fee, getCurrencyPrecision(feeCurrency))}
                  showDiscount={!!originalFeeAmount && originalFeeAmount !== fee}
                  currency={feeCurrency}
                />
              }
              data-test='fee-amount'
            />
          )}

          {Number(promoCodeDiscount) > 0 && (
            <DiscountSavingRow
              value={formatFloat(promoCodeDiscount, getCurrencyPrecision(feeCurrency))}
              currency={feeCurrency}
            />
          )}

          <SendMoneyAmountRow
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.NET_AMOUNT', 'Net amount')}
            value={
              <span>
                {formatFloat(processingAmount, getCurrencyPrecision(processingCurrency))}{' '}
                <span className='row-amount-currency'>{formatCurrencyForDisplay(processingCurrency)}</span>
              </span>
            }
            data-test='net-amount'
          />
        </div>

        <div className='withdraw-preview-container send-money-details'>
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.FROM', 'From account')}
            value={selectedAccount?.name}
            data-test='account-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.TO', 'To')}
            value={selectedRecipient?.holderName || selectedRecipient?.name}
            data-test='recipient-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.COUNTRY', 'Country')}
            value={translateCountryName(selectedRecipient?.country)}
            data-test='country'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.IBAN', 'IBAN')}
            value={formatIban(selectedRecipient?.iban)}
            data-test='iban'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.BANK_CODE', 'Bank code')}
            value={selectedRecipient?.bankCode}
            data-test='bank-code'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.BANK_NAME', 'Bank name')}
            value={selectedRecipient?.bankName}
            data-test='bank-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.BRANCH_CODE', 'Branch code')}
            value={selectedRecipient?.branchCode}
            data-test='branch-code'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.BRANCH_NAME', 'Branch name')}
            value={selectedRecipient?.branchName}
            data-test='branch-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.ACCOUNT_NUMBER', 'Account number')}
            value={selectedRecipient?.accountNumber}
            data-test='account-number'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.SWIFT', 'SWIFT')}
            value={selectedRecipient?.bicSwift}
            data-test='swift'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.EMAIL', 'Email')}
            value={selectedRecipient?.email}
            data-test='email'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.REFERENCE', 'Reference')}
            value={formValues.getValue('reference')}
            data-test='reference'
          />

          {accountingCategoryFF.isEnabled && (
            <MMLabelValue
              variant='withdrawal'
              label={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.CATEGORY', 'Payment category')}
              value={getAccountingCategoryLabel(formValues.getValue('accountingCategory'))}
              data-test='accounting-category'
            />
          )}
        </div>

        <TrustedRecipientCheckbox formValues={formValues} trustedRecipient={trustedRecipient} />

        <PreviewLayout.MobilePromo />

        <ErrorDisplay error={transferError} />

        <div className='preview-actions'>
          <MMButton
            type='button'
            size='large'
            variant='alternative'
            caption={tr('FRONTEND.WITHDRAW.WALLET_TRANSFER.PREVIEW.BUTTON_SEND', 'Send')}
            onClick={onSubmit}
            loading={transferFetching}
            data-test='button-send'
            fullWidth
          />
          <MMButton
            type='button'
            size='large'
            variant='tertiary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onCancel}
            disabled={transferFetching}
            data-test='button-cancel'
            fullWidth
          />
        </div>
      </div>
    </PreviewLayout>
  )
}

export default WalletTransferPreview
